/* ÜRETİLMİŞ DOSYA — ELLE DÜZENLEMEYİN.
   Kaynak: ris-api/RIS.UI/src/shared/template-builder
   Güncelleme: ris-api/scripts/sync-template-builder.sh */

import { Button, Dropdown, Empty, Space, Tag, Tooltip, Typography } from 'antd'
import {
    AlignLeftOutlined,
    ArrowDownOutlined,
    ArrowUpOutlined,
    BranchesOutlined,
    CheckSquareOutlined,
    ColumnHeightOutlined,
    CopyOutlined,
    DeleteOutlined,
    DownOutlined,
    EditOutlined,
    PictureOutlined,
    PlusOutlined,
    RetweetOutlined,
    RightOutlined,
    ScissorOutlined,
    TableOutlined,
} from '@ant-design/icons'
import { useState } from 'react'
import { BLOCK_TYPES, blockSummary, createBlock, moveItem, newId } from '../model'
import { CheckboxBlock, ConditionalHeader, ImageBlock, SignatureBlock, SpacerBlock, TextBlock } from './blocks/SimpleBlocks'
import { RepeatBlock, TableBlock } from './blocks/TableBlocks'

const TYPE_META = (t) => ({
    [BLOCK_TYPES.TEXT]: { label: t('Metin'), icon: <AlignLeftOutlined /> },
    [BLOCK_TYPES.CHECKBOX]: { label: t('Onay Kutusu'), icon: <CheckSquareOutlined /> },
    [BLOCK_TYPES.TABLE]: { label: t('Tablo'), icon: <TableOutlined /> },
    [BLOCK_TYPES.REPEAT]: { label: t('Tekrar Tablosu'), icon: <RetweetOutlined /> },
    [BLOCK_TYPES.IMAGE]: { label: t('Görsel'), icon: <PictureOutlined /> },
    [BLOCK_TYPES.PAGE_BREAK]: { label: t('Sayfa Sonu'), icon: <ScissorOutlined /> },
    [BLOCK_TYPES.SPACER]: { label: t('Boşluk'), icon: <ColumnHeightOutlined /> },
    [BLOCK_TYPES.SIGNATURE]: { label: t('İmza Alanı'), icon: <EditOutlined /> },
    [BLOCK_TYPES.CONDITIONAL]: { label: t('Koşullu Blok'), icon: <BranchesOutlined /> },
})

/** "Blok Ekle" menüsü — düz bir liste yerine amaca göre gruplanır. */
const ADD_GROUPS = (t, meta, allowConditional) => {
    const item = (type) => ({ key: type, label: meta[type].label, icon: meta[type].icon })

    const groups = [
        { key: 'g-content', type: 'group', label: t('İçerik'),
          children: [BLOCK_TYPES.TEXT, BLOCK_TYPES.TABLE, BLOCK_TYPES.REPEAT, BLOCK_TYPES.IMAGE].map(item) },
        { key: 'g-form', type: 'group', label: t('Form'),
          children: [BLOCK_TYPES.CHECKBOX, BLOCK_TYPES.SIGNATURE].map(item) },
        { key: 'g-layout', type: 'group', label: t('Düzen'),
          children: [BLOCK_TYPES.PAGE_BREAK, BLOCK_TYPES.SPACER].map(item) },
    ]

    if (allowConditional)
        groups.push({ key: 'g-logic', type: 'group', label: t('Mantık'), children: [item(BLOCK_TYPES.CONDITIONAL)] })

    return groups
}

/**
 * Blok listesi — şablonun ana çalışma alanı.
 *
 * Bloklar **katlanmış** başlar ve başlıkta içeriklerinin özetini gösterir. 20 bloklu bir onam formunda
 * hepsi açıkken (her metin bloğunda bir zengin metin editörü) liste gezilemez hâle geliyordu; katlama
 * bu yüzden varsayılan. Yeni eklenen blok otomatik açılır.
 *
 * Sıralama yukarı/aşağı düğmeleriyle yapılır — sürükle-bırak yerine bilinçli tercih: paket ek bir DnD
 * bağımlılığı getirmesin ve iki farklı React sürümünde (17/18) sorunsuz çalışsın.
 */
export default function BlockList({
    blocks = [],
    language,
    languages,
    placeholders,
    onChange,
    apiKey,
    registerInsert,
    t = (x) => x,
    depth = 0,
}) {
    const meta = TYPE_META(t)
    const [expanded, setExpanded] = useState(() => new Set())

    const toggle = (id) =>
        setExpanded((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })

    const update = (index, block) => onChange(blocks.map((b, i) => (i === index ? block : b)))
    const remove = (index) => onChange(blocks.filter((_, i) => i !== index))
    const move = (index, delta) => onChange(moveItem(blocks, index, index + delta))

    const add = (type) => {
        const block = createBlock(type, languages)
        onChange([...blocks, block])
        setExpanded((prev) => new Set(prev).add(block.id))
    }

    const duplicate = (index) => {
        const clone = JSON.parse(JSON.stringify(blocks[index]))
        const reId = (b) => {
            b.id = newId()
            ;(b.blocks || []).forEach(reId)
        }
        reId(clone)
        const next = [...blocks]
        next.splice(index + 1, 0, clone)
        onChange(next)
    }

    const renderBody = (block, index) => {
        const common = { block, language, languages, placeholders, t, onChange: (b) => update(index, b) }

        switch (block.type) {
            case BLOCK_TYPES.TEXT:
                return <TextBlock {...common} apiKey={apiKey} registerInsert={registerInsert} />
            case BLOCK_TYPES.CHECKBOX:
                return <CheckboxBlock {...common} />
            case BLOCK_TYPES.TABLE:
                return <TableBlock {...common} />
            case BLOCK_TYPES.REPEAT:
                return <RepeatBlock {...common} />
            case BLOCK_TYPES.IMAGE:
                return <ImageBlock {...common} />
            case BLOCK_TYPES.SIGNATURE:
                return <SignatureBlock {...common} />
            case BLOCK_TYPES.SPACER:
                return <SpacerBlock {...common} />
            case BLOCK_TYPES.PAGE_BREAK:
                return null
            case BLOCK_TYPES.CONDITIONAL:
                return (
                    <>
                        <ConditionalHeader {...common} />
                        <div className="tb-nested">
                            <BlockList
                                blocks={block.blocks || []}
                                language={language}
                                languages={languages}
                                placeholders={placeholders}
                                apiKey={apiKey}
                                registerInsert={registerInsert}
                                t={t}
                                depth={depth + 1}
                                onChange={(nested) => update(index, { ...block, blocks: nested })}
                            />
                        </div>
                    </>
                )
            default:
                return <Tag color="red">{t('Bilinmeyen blok')}: {block.type}</Tag>
        }
    }

    return (
        <div className="tb-blocklist">
            {blocks.length === 0 && (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('Henüz blok yok')} style={{ margin: '12px 0' }} />
            )}

            {blocks.map((block, index) => {
                const info = meta[block.type] || { label: block.type, icon: null }
                // Kimlik yoksa (elle yazılmış/eski tanım) sıra numarasına düş; aksi halde tüm
                // kimliksiz bloklar aynı anahtarı paylaşıp birlikte açılır.
                const key = block.id || `idx-${index}`
                const isOpen = expanded.has(key)
                const summary = blockSummary(block, language, t)
                const hasBody = block.type !== BLOCK_TYPES.PAGE_BREAK

                return (
                    <div key={key} className={`tb-block${isOpen ? ' tb-block-open' : ''}`}>
                        <div
                            className="tb-block-head"
                            onClick={() => hasBody && toggle(key)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (hasBody && (e.key === 'Enter' || e.key === ' ')) {
                                    e.preventDefault()
                                    toggle(key)
                                }
                            }}
                        >
                            <span className="tb-block-caret">
                                {hasBody ? (isOpen ? <DownOutlined /> : <RightOutlined />) : null}
                            </span>
                            <span className="tb-block-icon">{info.icon}</span>
                            <span className="tb-block-type">{info.label}</span>
                            <span className="tb-block-summary">
                                <Typography.Text type="secondary" ellipsis>{summary}</Typography.Text>
                            </span>

                            <Space size={0} className="tb-block-actions" onClick={(e) => e.stopPropagation()}>
                                <Tooltip title={t('Yukarı')}>
                                    <Button type="text" size="small" icon={<ArrowUpOutlined />}
                                        disabled={index === 0} onClick={() => move(index, -1)} />
                                </Tooltip>
                                <Tooltip title={t('Aşağı')}>
                                    <Button type="text" size="small" icon={<ArrowDownOutlined />}
                                        disabled={index === blocks.length - 1} onClick={() => move(index, 1)} />
                                </Tooltip>
                                <Tooltip title={t('Kopyala')}>
                                    <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => duplicate(index)} />
                                </Tooltip>
                                <Tooltip title={t('Sil')}>
                                    <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => remove(index)} />
                                </Tooltip>
                            </Space>
                        </div>

                        {isOpen && hasBody && <div className="tb-block-body">{renderBody(block, index)}</div>}
                    </div>
                )
            })}

            <Dropdown
                trigger={['click']}
                menu={{ items: ADD_GROUPS(t, meta, depth === 0), onClick: ({ key }) => add(key) }}
            >
                <Button type="dashed" icon={<PlusOutlined />} block style={{ marginTop: 8 }}>
                    {t('Blok Ekle')}
                </Button>
            </Dropdown>
        </div>
    )
}